import React, { useEffect, useState } from "react";
import { Button, Text } from "@showitinc/showit-wc";

import UIActions from "../../actions/UIActions";
import AppConstants from "../../constants/AppConstants";
import { generateFontsTagsFromDesignContext } from "../../utils/FontUtils";
import { FRAME_RESET_CSS } from "../ThemeStyleFrame";
import { resolveButtonStyles } from "../../utils/ButtonStyleResolver";
import styles from "./DesignSettingsButtonsPreview.module.css";
import PROPERTY_MAPS from "@/constants/PropertyMaps";
import { TEXT_FONTS, BUTTON_STYLES } from "../../constants/MergeConstants";
import { gatherProps } from "@/utils/Merge/MergeUtils.mjs";

const {
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  BUTTON_STYLE_PRIMARY,
  BUTTON_STYLE_SECONDARY,
  BUTTON_PANEL_TAB_STATE_HOVER,
  BUTTON_PANEL_TAB_STATE_REGULAR,
} = AppConstants;

const { STYLE_PROPS_THEME_BUTTONS, STYLE_PROPS_BUTTON_TEXT } = PROPERTY_MAPS;

const _getFontTags = (font, designContext) => {
  const tags = [];
  if (font) {
    tags.push(...generateFontsTagsFromDesignContext([font.toJS()], designContext));
  }
  tags.push(
    <style key="page-style" type="text/css">
      {FRAME_RESET_CSS}
    </style>,
  );
  return { tags };
};

/**
 * @param {Object} props
 * @param {string} props.buttonType - Type of button (primary/secondary)
 * @param {string} props.layout - Layout type (desktop/mobile)
 * @param {Object} props.theme - Theme configuration object
 * @param {Object} props.designContext - Design context for font generation
 * @param {string} props.selectedLayout - Currently selected layout
 * @param {Object} props.uiState - Current UI state
 * @param {Function} props.setUiState - UI state setter function
 */
const ButtonPreview = ({
  buttonType,
  layout,
  theme,
  designContext,
  selectedLayout,
  uiState,
  setUiState,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const buttonStyles = theme.getIn([BUTTON_STYLES, buttonType]);

  useEffect(() => {
    setIsHovered(uiState[`${buttonType}TabState`] === BUTTON_PANEL_TAB_STATE_HOVER);
  }, [uiState[`${buttonType}TabState`]]);

  const mergedProps = gatherProps(
    buttonStyles,
    layout,
    [...STYLE_PROPS_THEME_BUTTONS, ...STYLE_PROPS_BUTTON_TEXT],
    true,
  );

  const fontName = mergedProps.getIn(
    [...(isHovered ? ["hover"] : []), "text", "font"],
    mergedProps.getIn(["text", "font"]),
  );
  const font = theme.getIn([TEXT_FONTS, fontName]);
  const fontInfo = _getFontTags(font, designContext);

  const { regularCSS, hoverCSS } = resolveButtonStyles({
    buttonStyles,
    theme,
    layout,
    font,
    inherit: false,
  });

  const currentCSS = isHovered && layout === LAYOUT_DESKTOP ? hoverCSS : regularCSS;

  const { lineHeight, ...buttonContainerCSS } = currentCSS;

  return (
    <div className={styles["button-selection-container"]} style={{ position: "relative" }}>
      <div style={{ display: "none" }}>{fontInfo.tags}</div>
      <div
        style={buttonContainerCSS}
        type="button"
        onMouseEnter={() => layout === LAYOUT_DESKTOP && setIsHovered(true)}
        onMouseLeave={() =>
          layout === LAYOUT_DESKTOP &&
          uiState[`${buttonType}TabState`] === BUTTON_PANEL_TAB_STATE_REGULAR &&
          setIsHovered(false)
        }
        onClick={() => setUiState({ ...uiState, selectedPanel: buttonType })}
      >
        <span className={styles["button-text"]} style={{ lineHeight }}>
          {`${buttonType.charAt(0).toUpperCase() + buttonType.slice(1)} Button`}
        </span>
      </div>
      {selectedLayout === layout && uiState.selectedPanel === buttonType && (
        <div className={styles.selected} />
      )}
    </div>
  );
};

/**
 * @param {Object} props
 * @param {string} props.selectedLayout - Currently selected layout (desktop/mobile)
 * @param {Object} props.newTheme - Theme configuration object
 * @param {Object} props.designContext - Design context for font generation
 * @param {Object} props.uiState - Current UI state
 * @param {Function} props.setUiState - UI state setter function
 */
const DesignSettingsButtonsPreview = ({
  selectedLayout,
  newTheme,
  designContext,
  uiState,
  setUiState,
}) => {
  const _selectActiveLayout = (layout) => UIActions.selectActiveLayout({ layout, from: "panel" });

  return (
    <div className={styles.root}>
      {[LAYOUT_MOBILE, LAYOUT_DESKTOP].map((layout) => (
        <div
          key={layout}
          className={layout === LAYOUT_MOBILE ? styles.mobile : styles.desktop}
          onClick={() => _selectActiveLayout(layout)}
        >
          <Button
            variant="minimal"
            active={selectedLayout === layout}
            iconPrefix={layout === LAYOUT_DESKTOP ? "desktopDevice" : "mobileDevice"}
            className={styles.label}
            style={{ opacity: selectedLayout === layout ? 1 : 0.5 }}
          >
            <Text
              variant="body-2"
              style={{ color: selectedLayout === layout ? undefined : "#888" }}
            >
              {layout.toUpperCase()} PREVIEW
            </Text>
          </Button>

          <div className={styles["buttons-container"]}>
            <ButtonPreview
              buttonType={BUTTON_STYLE_PRIMARY}
              layout={layout}
              theme={newTheme}
              designContext={designContext}
              selectedLayout={selectedLayout}
              uiState={uiState}
              setUiState={setUiState}
            />
            <ButtonPreview
              buttonType={BUTTON_STYLE_SECONDARY}
              layout={layout}
              theme={newTheme}
              designContext={designContext}
              selectedLayout={selectedLayout}
              uiState={uiState}
              setUiState={setUiState}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default DesignSettingsButtonsPreview;
